/**
 * Left navigation rail. Wordmark plate on top, route list, harbour plate at the bottom.
 * The bottom plate is generated art, not a gradient -- it is the first thing that tells
 * the player this is a rendered world rather than a dashboard.
 *
 * TWO WIDTHS, since Phase 18. `compact` is the tablet: 76px, an icon and a short label per route,
 * no wordmark plate and no harbour plate, because at 1180 wide the 232px rail was a fifth of the
 * screen and the top bar had already lost the Heat cell to make room. Under 768 the rail is not
 * rendered at all; `BottomTabs` takes its place.
 *
 * The harbour plate also goes at any height under 900px (`.nav-plate` in skin.css): with it,
 * thirteen routes at 40px plus the wordmark block did not fit a 768px window, and Scanner and
 * Ledger scrolled below a fold nothing marked. A route the player cannot see is a route they do not
 * know exists.
 */

import { A } from '../art'
import { Icon, type IconName } from '../ui/Icon'
import { sz } from '../ui/viewport'
import type { Route } from './routes'
import { ROUTES } from './routes'

export function LeftNav({
  route,
  go,
  badges,
  compact = false,
}: {
  route: Route
  go: (r: Route) => void
  badges?: Partial<Record<Route, number>>
  compact?: boolean
}) {
  return (
    <nav
      aria-label="Main"
      style={{
        width: compact ? 76 : 'var(--rail)',
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--line)',
        background: 'linear-gradient(180deg, rgba(11,15,18,.9), rgba(5,7,9,.96))',
        position: 'relative',
      }}
    >
      {/* --- wordmark ---------------------------------------------------- */}
      {compact ? (
        <div style={{ padding: '14px 0 8px', flex: 'none', textAlign: 'center' }}>
          <div className="wordmark" style={{ fontSize: 22, letterSpacing: '.02em' }} aria-label="Ashport">
            A
          </div>
        </div>
      ) : (
        <div style={{ padding: '18px 16px 14px', flex: 'none' }}>
          <div className="wordmark" style={{ fontSize: 54 }}>
            Ashport
          </div>
          <div
            className="t-label"
            style={{
              marginTop: 6,
              fontSize: 9,
              letterSpacing: '.36em',
              color: 'var(--brass-lit)',
              borderTop: '1px solid var(--line-brass)',
              paddingTop: 6,
            }}
          >
            Online Crime RPG
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-mid)', fontStyle: 'italic' }}>
            Every block has a price.
          </div>
        </div>
      )}

      {/* --- routes ------------------------------------------------------ */}
      <div className="scroll-y" style={{ flex: 1, padding: '4px 0' }}>
        {ROUTES.map((r) => {
          const on = route === r.id
          return (
            <button
              key={r.id}
              onClick={() => go(r.id)}
              aria-current={on ? 'page' : undefined}
              aria-label={r.label}
              title={compact ? r.label : undefined}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: compact ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: compact ? 'center' : undefined,
                gap: compact ? 4 : 13,
                padding: compact ? '9px 2px' : '11px 16px',
                minHeight: compact ? 52 : undefined,
                border: 'none',
                borderLeft: `2px solid ${on ? 'var(--gold)' : 'transparent'}`,
                background: on
                  ? 'linear-gradient(90deg, rgba(214,168,75,.17), rgba(214,168,75,.02) 72%, transparent)'
                  : 'transparent',
                color: on ? 'var(--gold)' : 'var(--ink-mid)',
                font: compact ? `400 ${sz(8.5)}px/1 var(--f-head)` : '400 14px/1 var(--f-head)',
                letterSpacing: compact ? '.06em' : '.15em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'background 150ms ease, color 150ms ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (!on) e.currentTarget.style.background = 'rgba(255,255,255,.035)'
              }}
              onMouseLeave={(e) => {
                if (!on) e.currentTarget.style.background = 'transparent'
              }}
            >
              <Icon n={r.icon as IconName} s={compact ? 20 : 18} c={on ? 'var(--gold)' : 'var(--ink-dim)'} />
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {r.label}
              </span>
              {badges?.[r.id] ? (
                <span
                  style={{
                    marginLeft: compact ? 0 : 'auto',
                    minWidth: 17,
                    height: 17,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 10,
                    fontFamily: 'var(--f-head)',
                    background: 'var(--arrest)',
                    color: '#fff',
                    borderRadius: 2,
                  }}
                >
                  {badges[r.id]}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      {/* --- harbour plate ----------------------------------------------- */}
      {!compact && (
        <div className="nav-plate" style={{ flex: 'none', position: 'relative', height: 196, overflow: 'hidden' }}>
          <img
            src={A('brand.skyline')}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '62% 60%', opacity: 0.85 }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, var(--coal) 0%, rgba(5,7,9,.35) 34%, rgba(5,7,9,.9) 82%, var(--coal) 100%)',
            }}
          />
          <div style={{ position: 'absolute', inset: 'auto 0 16px', textAlign: 'center' }}>
            <div
              className="t-head"
              style={{ fontSize: 13, color: 'var(--brass-lit)', letterSpacing: '.24em' }}
            >
              Ashport Harbor
            </div>
            <div className="t-label" style={{ fontSize: 9, letterSpacing: '.36em', marginTop: 3 }}>
              Est. 1912
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
